import React, { useState } from "react";
import {
  Search,
  Loader2,
  Download,
  User,
  Building2,
  MapPin,
  Fingerprint,
  Copy,
  Filter,
  UserRound,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { toast } from "sonner";
import { Reveal, PageTransition } from "@/components/Reveal";
import { PageHero, CTABand } from "@/components/CTA";
import { MoreTools } from "@/components/MoreTools";
import { Seo } from "@/components/Seo";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY",
];

const faqs = [
  {
    q: "What is NPI lookup by name?",
    a: "NPI lookup by name is a free search that finds a healthcare provider's National Provider Identifier (NPI) using their first name, last name, or organization name — without needing the NPI number itself. Hewto.ai's NPI lookup by name uses the CMS NPPES public registry so you can find a doctor's NPI, look up a hospital's NPI, or search a group practice's NPI in seconds.",
  },
  {
    q: "How do I find a doctor's NPI number by name?",
    a: "To find a doctor's NPI number by name, enter their first and last name above and (optionally) narrow by state, city, or taxonomy (specialty). The tool queries the CMS NPPES registry live and returns every matching provider — with their 10-digit NPI, credential, specialty, and practice address — so you can pick the right doctor and copy the NPI in one click.",
  },
  {
    q: "How do I look up a hospital or organization's NPI?",
    a: "Switch the entity type to Organization (NPI-2) and enter the hospital, clinic, or group-practice name. Add a state and city if you want to filter to a specific location. The tool returns organization NPIs from the CMS NPPES registry with the practice address and taxonomy so you can find the right facility.",
  },
  {
    q: "Can I search partial names?",
    a: "Yes. Add an asterisk (*) to search a partial name — for example, last name 'SMIT*' will match Smith, Smithers, and Smithson. Names must be at least 2 characters before the wildcard. Combine name filters with state, city, and specialty to narrow the results.",
  },
  {
    q: "Is this NPI lookup by name free?",
    a: "Yes. Hewto.ai's NPI lookup by name tool is 100% free — no signup, no API key. It's a live proxy over the official CMS NPPES public API, so you get up-to-date NPI records including the provider's name, taxonomy (specialty), practice address, phone, and status.",
  },
  {
    q: "What's the difference between an individual (NPI-1) and organization (NPI-2) lookup?",
    a: "NPI-1 is an individual provider (a doctor, dentist, PA, NP, therapist, etc.) — search by first and last name. NPI-2 is an organization (a hospital, clinic, group practice, DME supplier, pharmacy) — search by organization name. The lookup form lets you filter by entity type so you get exactly the record you need.",
  },
  {
    q: "Can I filter NPI search by specialty or taxonomy?",
    a: "Yes. Use the Taxonomy field to filter by specialty description — for example 'Family Medicine', 'Internal Medicine', 'Dentist', 'Cardiology', 'Physical Therapist'. This is the CMS NPPES taxonomy classification, so it matches the same specialty labels used on claim forms.",
  },
  {
    q: "Does Hewto.ai store the NPI searches I run?",
    a: "No. Every search is proxied live through Hewto.ai to the CMS NPPES public registry and results are returned in real time. We do not persist your search queries or the returned records.",
  },
];

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const softwareJsonLd = {
  "@context": "https://schema.org",
  "@type": ["WebApplication", "SoftwareApplication"],
  name: "Hewto.ai NPI Lookup by Name",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  description:
    "Free NPI lookup by provider or organization name — search the CMS NPPES registry live and get any doctor's or hospital's NPI number, taxonomy, and practice address.",
  offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
};

const toCsv = (rows) => {
  const header = [
    "npi", "entity_type", "name", "credential", "taxonomy",
    "license", "status", "address_line1", "city", "state", "postal_code", "phone",
  ];
  const esc = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [header.join(",")];
  for (const r of rows) {
    lines.push([
      r.npi, r.entity_type, r.name, r.credential, r.taxonomy?.desc,
      r.taxonomy?.license, r.status,
      r.address?.line1, r.address?.city, r.address?.state, r.address?.postal_code,
      r.address?.phone,
    ].map(esc).join(","));
  }
  return lines.join("\n");
};

const ResultCard = ({ r }) => {
  const copyNpi = () => {
    navigator.clipboard.writeText(r.npi);
    toast.success(`NPI ${r.npi} copied`);
  };
  const isOrg = r.entity_type === "Organization";
  return (
    <div
      data-testid={`npi-result-${r.npi}`}
      className="group rounded-2xl border border-slate-200 bg-white p-6 transition-all duration-200 hover:-translate-y-0.5 hover:border-brand-200 hover:shadow-lg hover:shadow-brand/5"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 px-2 py-0.5 text-xs font-semibold text-brand-700">
            {isOrg ? <Building2 className="h-3 w-3" /> : <UserRound className="h-3 w-3" />}
            {r.entity_type || "Provider"}
          </span>
          <h3 className="mt-2 text-base font-semibold text-slate-900">{r.name || "—"}</h3>
        </div>
        <button
          type="button"
          onClick={copyNpi}
          data-testid={`copy-npi-${r.npi}`}
          className="flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 font-mono text-xs font-semibold text-slate-700 transition-colors hover:border-brand hover:text-brand"
          title="Copy NPI"
        >
          {r.npi}
          <Copy className="h-3 w-3" />
        </button>
      </div>
      <div className="mt-4 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
        <div>
          <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-slate-400">
            <Fingerprint className="h-3 w-3" /> Taxonomy
          </p>
          <p className="mt-1 text-sm text-slate-700">{r.taxonomy?.desc || "—"}</p>
          {r.taxonomy?.license && (
            <p className="font-mono text-xs text-slate-500">
              {r.taxonomy?.state ? `${r.taxonomy.state} · ` : ""}License {r.taxonomy.license}
            </p>
          )}
        </div>
        <div>
          <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-slate-400">
            <MapPin className="h-3 w-3" /> Practice address
          </p>
          <p className="mt-1 text-sm leading-relaxed text-slate-700">
            {r.address?.line1}
            {r.address?.line2 ? `, ${r.address.line2}` : ""}
            <br />
            {[r.address?.city, r.address?.state, r.address?.postal_code].filter(Boolean).join(", ")}
            {r.address?.phone ? (
              <span className="mt-1 block font-mono text-xs text-slate-500">{r.address.phone}</span>
            ) : null}
          </p>
        </div>
      </div>
      <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
        <span>Status: <span className="font-semibold text-slate-700">{r.status === "A" ? "Active" : r.status || "—"}</span></span>
        {r.credential && <span>Credential: <span className="font-semibold text-slate-700">{r.credential}</span></span>}
      </div>
    </div>
  );
};

const NPILookupByName = () => {
  const [entity, setEntity] = useState("NPI-1");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [orgName, setOrgName] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [taxonomy, setTaxonomy] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);

  const runSearch = async (e) => {
    e?.preventDefault?.();
    const params = new URLSearchParams();
    params.set("enumeration_type", entity);
    if (entity === "NPI-1") {
      if (!firstName.trim() && !lastName.trim()) {
        toast.error("Enter a first or last name");
        return;
      }
      if (firstName) params.set("first_name", firstName.trim());
      if (lastName) params.set("last_name", lastName.trim());
    } else {
      if (!orgName.trim()) {
        toast.error("Enter an organization name");
        return;
      }
      params.set("organization_name", orgName.trim());
    }
    if (city) params.set("city", city.trim());
    if (state) params.set("state", state);
    if (taxonomy) params.set("taxonomy_description", taxonomy.trim());
    params.set("limit", "50");

    setLoading(true);
    setResults([]);
    setSearched(true);
    try {
      const res = await fetch(`${API}/npi/search?${params.toString()}`);
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || "Search failed");
        return;
      }
      setResults(data.results || []);
      toast.success(`Found ${data.count} matching NPI${data.count === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error("Search failed — please try again");
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = () => {
    const csv = toCsv(results);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `npi-search-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <PageTransition>
      <Seo
        title="NPI Lookup by Name — Free Provider & Doctor NPI Search | Hewto.ai"
        description="Free NPI lookup by name — find any doctor's, dentist's, or hospital's NPI number by searching the CMS NPPES registry live. Filter by first name, last name, organization, state, city, or specialty. No signup, no API key."
        keywords="npi lookup by name, npi search by name, find npi by name, doctor npi lookup, provider npi lookup, hospital npi lookup, organization npi lookup, nppes search by name, cms npi search, npi lookup by first name, npi lookup by last name, npi lookup by organization, npi lookup by state, npi lookup by specialty, npi lookup by taxonomy, free npi search, npi directory, look up doctor npi number, find a provider's npi, npi 1 lookup by name, npi 2 lookup by name"
        canonical="https://www.hewto.ai/tools/npi-lookup-by-name"
        jsonLd={[faqJsonLd, softwareJsonLd]}
      />

      <PageHero
        eyebrow="Free tool · NPI Lookup by Name"
        title="NPI Lookup by Name"
        subtitle="Find any US healthcare provider's or organization's NPI number by name — Hewto.ai's free NPI lookup by name searches the CMS NPPES registry live. Filter by first name, last name, organization, state, city, or specialty. No signup, no API key."
      />

      <section className="mx-auto max-w-5xl px-5 pb-16 sm:px-8">
        <Reveal>
          <form
            onSubmit={runSearch}
            className="rounded-3xl border border-slate-200 bg-white p-6 shadow-lg shadow-brand/5 sm:p-8"
          >
            {/* Entity toggle */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                data-testid="entity-tab-npi1"
                onClick={() => setEntity("NPI-1")}
                className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                  entity === "NPI-1"
                    ? "bg-brand text-white shadow-sm"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                <User className="h-4 w-4" /> Individual (NPI-1)
              </button>
              <button
                type="button"
                data-testid="entity-tab-npi2"
                onClick={() => setEntity("NPI-2")}
                className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                  entity === "NPI-2"
                    ? "bg-brand text-white shadow-sm"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                <Building2 className="h-4 w-4" /> Organization (NPI-2)
              </button>
            </div>

            {/* Name fields */}
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {entity === "NPI-1" ? (
                <>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">First name</label>
                    <Input
                      data-testid="npi-first-name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="e.g. John"
                      className="mt-2 h-11 rounded-xl"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Last name</label>
                    <Input
                      data-testid="npi-last-name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="e.g. Smith (add * for wildcard)"
                      className="mt-2 h-11 rounded-xl"
                    />
                  </div>
                </>
              ) : (
                <div className="sm:col-span-2">
                  <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Organization name</label>
                  <Input
                    data-testid="npi-org-name"
                    value={orgName}
                    onChange={(e) => setOrgName(e.target.value)}
                    placeholder="e.g. Cleveland Clinic (add * for wildcard)"
                    className="mt-2 h-11 rounded-xl"
                  />
                </div>
              )}
            </div>

            {/* Filters */}
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">City</label>
                <Input
                  data-testid="npi-city"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="Optional"
                  className="mt-2 h-11 rounded-xl"
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">State</label>
                <Select value={state} onValueChange={(v) => setState(v === "any" ? "" : v)}>
                  <SelectTrigger data-testid="npi-state" className="mt-2 h-11 rounded-xl">
                    <SelectValue placeholder="Any state" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="any">Any state</SelectItem>
                    {US_STATES.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Taxonomy / specialty</label>
                <Input
                  data-testid="npi-taxonomy"
                  value={taxonomy}
                  onChange={(e) => setTaxonomy(e.target.value)}
                  placeholder="e.g. Family Medicine"
                  className="mt-2 h-11 rounded-xl"
                />
              </div>
            </div>

            <div className="mt-6 flex items-center gap-3">
              <Button
                type="submit"
                data-testid="npi-search-btn"
                disabled={loading}
                className="h-11 rounded-xl bg-brand px-6 font-semibold text-white hover:bg-brand-600"
              >
                {loading ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Searching NPPES</>
                ) : (
                  <><Search className="mr-2 h-4 w-4" /> Search NPI Registry</>
                )}
              </Button>
              <p className="text-xs text-slate-500">
                Tip: add <code className="rounded bg-slate-100 px-1 py-0.5 font-mono">*</code> for partial matches (e.g. <code className="rounded bg-slate-100 px-1 py-0.5 font-mono">Smit*</code>)
              </p>
            </div>
          </form>
        </Reveal>
      </section>

      {/* Results */}
      {searched && (
        <section className="mx-auto max-w-5xl px-5 pb-20 sm:px-8">
          <div className="flex items-center justify-between">
            <p data-testid="npi-results-count" className="text-sm font-semibold text-slate-600">
              {loading ? "Searching…" : `${results.length} result${results.length === 1 ? "" : "s"}`}
            </p>
            {results.length > 0 && (
              <Button
                variant="outline"
                onClick={exportCsv}
                data-testid="npi-export-csv"
                className="rounded-full"
              >
                <Download className="mr-2 h-4 w-4" /> Export CSV
              </Button>
            )}
          </div>
          {!loading && results.length === 0 && (
            <div className="mt-6 rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center">
              <Filter className="mx-auto h-6 w-6 text-slate-400" />
              <p className="mt-3 text-sm font-semibold text-slate-700">No providers matched those filters</p>
              <p className="mt-1 text-sm text-slate-500">
                Try a wildcard (e.g. <code className="rounded bg-white px-1 py-0.5 font-mono">Smit*</code>) or remove some filters.
              </p>
            </div>
          )}
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
            {results.map((r) => (
              <ResultCard key={r.npi} r={r} />
            ))}
          </div>
        </section>
      )}

      {/* HOW IT WORKS */}
      <section className="bg-slate-50 py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <div className="mx-auto max-w-2xl text-center">
              <span className="inline-flex items-center gap-2 rounded-full border border-brand-200/40 bg-brand-50 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand-700">
                <Sparkles className="h-3.5 w-3.5" /> How it works
              </span>
              <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                Find any provider's NPI in three steps
              </h2>
            </div>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
            {[
              {
                n: "01",
                title: "Pick Individual or Organization",
                desc: "NPI-1 for a doctor, dentist, PA, NP, or therapist. NPI-2 for a hospital, clinic, group practice, DME, or pharmacy.",
              },
              {
                n: "02",
                title: "Enter the name + filters",
                desc: "Type a first name, last name, or organization name. Narrow by state, city, or taxonomy (specialty). Use * for partial matches.",
              },
              {
                n: "03",
                title: "Get NPIs live from NPPES",
                desc: "Every matching provider shows up with their 10-digit NPI, taxonomy, credential, license, address, and phone. Copy or export to CSV.",
              },
            ].map((s, i) => (
              <Reveal key={s.n} delay={i * 0.08}>
                <div className="h-full rounded-2xl border border-slate-200 bg-white p-8">
                  <span className="font-mono text-sm font-semibold text-brand-600">{s.n}</span>
                  <h3 className="mt-3 text-lg font-semibold text-slate-900">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-4xl px-5 py-24 sm:px-8">
        <Reveal>
          <span className="text-sm font-semibold uppercase tracking-widest text-brand-600">
            NPI lookup by name FAQ
          </span>
          <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Common provider-lookup questions
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <Accordion type="single" collapsible className="mt-8" data-testid="npi-name-faq">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-b border-slate-200">
                <AccordionTrigger
                  data-testid={`npi-name-faq-trigger-${i}`}
                  className="text-left text-base font-semibold text-slate-900 hover:no-underline"
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </section>

      <MoreTools current="/tools/npi-lookup-by-name" />

      <CTABand />
    </PageTransition>
  );
};

export default NPILookupByName;
